import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Heart, PartyPopper, Check } from 'lucide-react';
import { useBands, useSwipes } from '../../hooks/useBands';
import { BandCard } from '../../components/BandCard';
import { Button } from '../../components/ui';
import type { SwipeAction } from '../../types/database';
import styles from './Discover.module.css';

export function Discover() {
  const navigate = useNavigate();
  const { unswipedBands, loading, error } = useBands();
  const { recordSwipe } = useSwipes();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnimation, setShowAnimation] = useState<'match' | 'connect' | null>(null);
  const [animationBandName, setAnimationBandName] = useState('');

  const currentBand = unswipedBands[currentIndex];

  const handleAction = useCallback(
    async (action: SwipeAction, timeSpent: number, expanded: boolean) => {
      if (!currentBand) return;

      await recordSwipe(currentBand.band.id, action, timeSpent, expanded);

      if (action === 'match') {
        setAnimationBandName(currentBand.band.name);
        setShowAnimation('match');
        setTimeout(() => {
          setShowAnimation(null);
          setCurrentIndex((i) => i + 1);
        }, 1200);
      } else if (action === 'connect') {
        setAnimationBandName(currentBand.band.name);
        setShowAnimation('connect');
        setTimeout(() => {
          setShowAnimation(null);
          setCurrentIndex((i) => i + 1);
        }, 1500);
      } else {
        setCurrentIndex((i) => i + 1);
      }
    },
    [currentBand, recordSwipe]
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Finding your matches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!currentBand) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>
          <PartyPopper size={64} className={styles.emptyIcon} />
          <h2>You've seen all bands!</h2>
          <p>Check out your matches to compare bands and make your decision.</p>
          <Button onClick={() => navigate('/matches')}>View My Matches</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.profileBtn} onClick={() => navigate('/profile')}>
          <User size={20} />
        </button>
        <img src="/logo-small.png" alt="Mas Match" className={styles.logo} />
        <button className={styles.matchesBtn} onClick={() => navigate('/matches')}>
          <Heart size={16} />
          Matches
        </button>
      </header>

      {/* Card stack */}
      <div className={styles.cardStack}>
        {/* Show next card behind current */}
        {unswipedBands[currentIndex + 1] && (
          <div className={styles.nextCard}>
            <div className={styles.nextCardPlaceholder} />
          </div>
        )}

        {/* Current card */}
        <BandCard
          band={currentBand.band}
          score={currentBand.totalScore}
          reasons={currentBand.reasons}
          breakdown={currentBand.breakdown}
          onAction={handleAction}
        />
      </div>

      {/* Match animation overlay */}
      {showAnimation === 'match' && (
        <div className={styles.matchOverlay}>
          <div className={styles.matchContent}>
            <Heart size={80} className={styles.matchIcon} />
            <h2>Matched!</h2>
            <p>You're interested in {animationBandName}</p>
          </div>
        </div>
      )}

      {/* Connect animation overlay */}
      {showAnimation === 'connect' && (
        <div className={`${styles.matchOverlay} ${styles.connectOverlay}`}>
          <div className={styles.matchContent}>
            <Check size={80} className={styles.connectIcon} />
            <h2>Connected!</h2>
            <p>You've decided on {animationBandName}</p>
          </div>
        </div>
      )}
    </div>
  );
}
